
import { Task } from '@/types/task';
import { supabase } from '@/integrations/supabase/client';
import { syncTasksWithSupabase } from '@/services/taskService';

const TASKS_KEY = 'clockthat_tasks';
const FREQUENT_TASKS_KEY = 'clockthat_frequent_tasks';

interface FrequentTask {
  title: string;
  duration: number;
  count: number;
}

// Helper function to check if we're running in a mobile browser
const isMobileBrowser = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Check if we have a valid user session
const hasUserSession = async (): Promise<boolean> => {
  const { data } = await supabase.auth.getSession();
  return !!data.session;
};

export const saveTasks = async (tasks: Task[]): Promise<Task[]> => {
  try {
    // Ensure all completed tasks have a completedAt timestamp
    const tasksWithDates = tasks.map(task => {
      if (task.completed && !task.completedAt) {
        return { ...task, completedAt: new Date().toISOString() };
      }
      // Make sure createdAt exists for all tasks
      if (!task.createdAt) {
        return { ...task, createdAt: new Date().toISOString() };
      }
      return task;
    });
    
    // Log number of completed tasks for debugging
    const completedTaskCount = tasksWithDates.filter(task => task.completed).length;
    console.log(`Saving ${tasksWithDates.length} tasks (${completedTaskCount} completed)`);
    
    // Always save to localStorage if available
    localStorage.setItem(TASKS_KEY, JSON.stringify(tasksWithDates));
    console.log('Tasks saved to localStorage:', tasksWithDates.length);
    
    // If user is logged in, also sync to Supabase
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      try {
        await syncTasksWithSupabase(tasksWithDates);
        console.log('Tasks synced with Supabase');
      } catch (error) {
        console.error('Error syncing with Supabase:', error);
      }
    }
    
    return tasksWithDates;
  } catch (error) {
    console.error('Error saving tasks:', error);
    
    // If localStorage fails (which can happen in some mobile browsers),
    // notify the user to log in for task persistence
    if (isMobileBrowser()) {
      console.warn('Mobile storage may be limited. Please sign in to ensure your tasks are saved.');
    }
    
    return [];
  }
};

export const loadTasks = async (): Promise<Task[]> => {
  try {
    // Try to get tasks from localStorage first
    const localTasks = localStorage.getItem(TASKS_KEY);
    let parsedLocalTasks: Task[] = localTasks ? JSON.parse(localTasks) : [];
    
    // Ensure all tasks have proper date fields
    parsedLocalTasks = parsedLocalTasks.map(task => {
      // Fix completed tasks without completedAt date
      if (task.completed && !task.completedAt) {
        return { ...task, completedAt: task.createdAt || new Date().toISOString() };
      }
      return task;
    });
    
    // Log number of completed tasks for debugging
    const completedTaskCount = parsedLocalTasks.filter(task => task.completed).length;
    const activeTasks = parsedLocalTasks.filter(task => !task.completed).length;
    console.log(`Loaded ${parsedLocalTasks.length} total tasks - ${completedTaskCount} completed, ${activeTasks} active`);
    
    // If user is logged in, try to get tasks from Supabase
    const isLoggedIn = await hasUserSession();
    if (isLoggedIn) {
      console.log('User is logged in, attempting to load tasks from Supabase');
      // We'll return Supabase tasks in fetchUserTasks if available
      return parsedLocalTasks;
    }
    
    return parsedLocalTasks;
  } catch (error) {
    console.error('Error loading tasks:', error);
    return [];
  }
};

export const updateFrequentTasks = (task: Task) => {
  try {
    const frequentTasks = loadFrequentTasksSync();
    const existingTask = frequentTasks.find(t => t.title === task.title && t.duration === task.duration);
    
    if (existingTask) {
      existingTask.count += 1;
      localStorage.setItem(FREQUENT_TASKS_KEY, JSON.stringify(frequentTasks));
    } else {
      const newFrequentTasks = [...frequentTasks, { 
        title: task.title, 
        duration: task.duration, 
        count: 1 
      }];
      localStorage.setItem(FREQUENT_TASKS_KEY, JSON.stringify(newFrequentTasks));
    }
  } catch (error) {
    console.error('Error updating frequent tasks:', error);
  }
};

// Non-async version for internal use
export const loadFrequentTasksSync = (): FrequentTask[] => {
  try {
    const tasks = localStorage.getItem(FREQUENT_TASKS_KEY);
    return tasks ? JSON.parse(tasks) : [];
  } catch (error) {
    console.error('Error loading frequent tasks:', error);
    return [];
  }
};

// Keep the original function for API consistency
export const loadFrequentTasks = (): FrequentTask[] => {
  return loadFrequentTasksSync();
};

export const getPopularTasks = (): FrequentTask[] => {
  const tasks = loadFrequentTasksSync();
  return tasks.filter(task => task.count >= 3)
    .sort((a, b) => b.count - a.count);
};

// Utility function to ensure task has required date fields
export const prepareTaskForSave = (task: Task): Task => {
  const now = new Date();
  
  // Ensure createdAt exists
  if (!task.createdAt) {
    task.createdAt = now.toISOString();
  }
  
  // Set completedAt if the task is completed but doesn't have a completion date
  if (task.completed && !task.completedAt) {
    task.completedAt = now.toISOString();
  }
  
  // Set default values for reminder and completion sound if they don't exist
  if (task.reminderSet === undefined) {
    task.reminderSet = true;
  }
  
  if (task.completionSound === undefined) {
    task.completionSound = true;
  }
  
  return task;
};
